import { lazy, Suspense, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { flushSync } from 'react-dom'
import { Outlet, useLocation, useNavigate, useParams } from 'react-router-dom'
import ContactField3D from './ContactField3D'
import { IS_OWNER } from '../lib/ownerMode'
import HubSlideHeading from './HubSlideHeading'
import PanelTransformTuner, {
  CONTACT_LAYOUT_DEFAULTS,
  CONTACT_STORAGE_KEY,
  FLAT_MEDIA_LAYOUT_DEFAULTS,
  applyContactLayoutVars,
  applyFlatMediaLayoutVars,
  loadContactLayout,
  loadFlatMediaLayout,
} from './PanelTransformTuner'
import { getMediaUrl } from './ProjectMedia'
import {
  contactContent,
  featuredProjects,
  showreelContent,
  siteInfo,
} from '../data/portfolio'
import {
  HUB_SCENE_RESET_EVENT,
  emitCharacterReaction,
  CONTACT_FORM_EVENT,
  emitContactForm,
  emitProjectExploreHandoff,
  emitShowreelWatch,
  hasWatchedShowreelBefore,
  markShowreelWatched,
} from '../lib/hubTools'
import {
  begin as beginTransition,
  end as endTransition,
  hasTransitionKind,
  isBusy as isTransitionBusy,
  registerNavigate,
  registerTheaterClose,
  requestNavigation,
  setRoute as setTransitionRoute,
  subscribeTransitionCoordinator,
  TRANSITION_KINDS,
} from '../lib/transitionCoordinator'
import { whenOpeningStep } from '../lib/openingSequence'
import { MOTION } from '../lib/motion'
import { IS_DESKTOP_EXPERIENCE } from '../lib/deviceGate'
import { usePageTitle } from '../lib/hooks/usePageTitle'
import { getProjectDetailsPath, getProjectPath } from '../lib/utils/projects'

const ContactLevel = lazy(() => import('./ContactLevel'))

const HUB_SCROLL_COMMIT_PX = 720
const HUB_SCROLL_WHEEL_IDLE_MS = 180
const HUB_SCROLL_SPRING_MS = MOTION.ms.spring
const HUB_SCROLL_SMOOTH_LERP = 0.24
const HUB_VIDEO_PLATE_CLASS =
  'hub-flat-backplate hub-flat-backplate--media site-frame'

const HUB_NAV_BTN_CLASS =
  'hub-flat-explore-btn hub-flat-nav-btn inline-flex items-center justify-center disabled:cursor-not-allowed disabled:opacity-30 disabled:hover:opacity-30'

const FORMSPREE_ENDPOINT = 'https://formspree.io/f/xqpkajgb'

const HubNavArrowUp = () => (
  <svg
    viewBox="0 0 16 16"
    className="h-4 w-4"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    aria-hidden="true"
  >
    <path d="M4 10l4-4 4 4" />
  </svg>
)

const HubNavArrowDown = () => (
  <svg
    viewBox="0 0 16 16"
    className="h-4 w-4"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    aria-hidden="true"
  >
    <path d="M4 6l4 4 4-4" />
  </svg>
)

const HubNavUpButton = ({ onClick, disabled, label, className = '' }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    aria-label={label}
    className={`${HUB_NAV_BTN_CLASS}${className ? ` ${className}` : ''}`}
  >
    <HubNavArrowUp />
  </button>
)

const HubNavDownButton = ({ onClick, disabled, label, className = '' }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    aria-label={label}
    className={`${HUB_NAV_BTN_CLASS}${className ? ` ${className}` : ''}`}
  >
    <HubNavArrowDown />
  </button>
)

/** Hub project previews — loops while its slide is active. */
const HubProjectVideo = ({ src, poster, isActive }) => {
  const videoRef = useRef(null)

  useEffect(() => {
    const video = videoRef.current
    if (!video) return undefined

    const playIfActive = () => {
      if (!isActive) return
      video.preload = 'auto'
      video.play().catch(() => {})
    }

    const pauseAndPark = () => {
      video.pause()
      try {
        video.currentTime = 0
      } catch {
        // Ignore seek errors on some codecs mid-load
      }
    }

    const handleVisibility = () => {
      if (document.hidden) {
        video.pause()
        return
      }
      playIfActive()
    }

    document.addEventListener('visibilitychange', handleVisibility)

    if (isActive) playIfActive()
    else pauseAndPark()

    return () => {
      document.removeEventListener('visibilitychange', handleVisibility)
    }
  }, [isActive])

  return (
    <video
      ref={videoRef}
      className="hub-project-preview aspect-video w-full origin-center bg-secondary object-cover transition-transform duration-300 ease-out group-hover:scale-[1.06] group-focus-visible:scale-[1.06] motion-reduce:transition-none motion-reduce:group-hover:scale-100 motion-reduce:group-focus-visible:scale-100"
      src={src}
      poster={poster || undefined}
      loop
      muted
      playsInline
      preload="none"
      aria-hidden="true"
      tabIndex={-1}
    />
  )
}

const HUB_SLIDE_KEYS = [
  'showreel',
  ...featuredProjects.map((project) => `project-${project.id}`),
  'contact',
]

const buildSlidePark = (activeKey) => {
  const next = {}
  HUB_SLIDE_KEYS.forEach((key) => {
    next[key] = key === activeKey ? 'home' : 'above'
  })
  return next
}

const readPrefersReducedMotion = () =>
  typeof window !== 'undefined'
  && window.matchMedia('(prefers-reduced-motion: reduce)').matches

const normalizeWheelDelta = (event) => {
  let { deltaY } = event
  if (event.deltaMode === WheelEvent.DOM_DELTA_LINE) {
    deltaY *= 16
  } else if (event.deltaMode === WheelEvent.DOM_DELTA_PAGE) {
    deltaY *= window.innerHeight
  }
  return deltaY
}

const isHubEditableTarget = (target) =>
  Boolean(
    target?.closest?.(
      'input, textarea, select, [contenteditable="true"], .hub-tool-panel, .site-sidebar-scroll',
    ),
  )

const buildInitialSlidePark = (activeKey) => {
  const park = buildSlidePark(activeKey)
  if (!readPrefersReducedMotion()) park[activeKey] = 'above'
  return park
}

const pathForHubSlide = (key) => {
  if (key === 'contact') return '/contact'
  if (key?.startsWith('project-')) return getProjectPath(key.slice('project-'.length))
  return '/'
}

const labelForHubSlide = (key) => {
  if (key === 'showreel') return 'showreel'
  if (key === 'contact') return 'contact'
  const id = key?.startsWith('project-') ? key.slice('project-'.length) : ''
  const project = featuredProjects.find((item) => item.id === id)
  return project?.title?.toLowerCase() || 'section'
}

/** Anim-8 uses Song; Sakira Mods (and other routes) keep Sakira. */
const characterKeyForProject = (projectId) =>
  projectId === 'anim-8' ? 'song' : 'sakira'

const readEnterColor = () => {
  const styles = getComputedStyle(document.documentElement)
  return (
    styles.getPropertyValue('--color-scene-sky').trim() ||
    styles.getPropertyValue('--color-bg-primary').trim() ||
    '#121212'
  )
}

const SHOWREEL_ASPECT = 16 / 9
const SHOWREEL_THEATER_MS = MOTION.ms.theater
const SHOWREEL_THEATER_INSET = 24

const readStageUi = () => document.querySelector('.stage-ui')

const readStageScale = (spaceEl = readStageUi()) => {
  const space = spaceEl?.getBoundingClientRect()
  const scaleX = space && spaceEl?.offsetWidth ? space.width / spaceEl.offsetWidth : 1
  const scaleY = space && spaceEl?.offsetHeight ? space.height / spaceEl.offsetHeight : 1
  return { scaleX, scaleY }
}

const readMainPaneRect = () => {
  const pane = document.querySelector('.site-main-scroll')
  const rect = pane?.getBoundingClientRect()
  if (rect?.width && rect?.height) {
    const top = rect.top
    const height = Math.max(1, Math.min(rect.height, window.innerHeight - top))
    return {
      left: rect.left,
      top,
      width: rect.width,
      height,
      right: rect.left + rect.width,
      bottom: top + height,
    }
  }

  const sidebar = document.querySelector('.site-sidebar-scroll')
  const dock = document.querySelector('.site-bottom-dock')
  const sidebarRect = sidebar?.getBoundingClientRect()
  const dockRect = dock?.getBoundingClientRect()
  const left = sidebarRect?.width > 0 ? sidebarRect.right : 0
  const bottom = dockRect?.height > 0 ? dockRect.top : window.innerHeight
  const width = Math.max(1, window.innerWidth - left)
  const height = Math.max(1, bottom)
  return {
    left,
    top: 0,
    width,
    height,
    right: left + width,
    bottom,
  }
}

const readShowreelTheaterRect = () => {
  const stage = document.querySelector('.stage')
  const pane = stage?.getBoundingClientRect()
  const bounds =
    pane?.width && pane?.height ? pane : readMainPaneRect()
  const platePad = 32
  const maxBottom = bounds.bottom - SHOWREEL_THEATER_INSET
  const minLeft = bounds.left + SHOWREEL_THEATER_INSET
  const maxRight = bounds.right - SHOWREEL_THEATER_INSET
  const minTop = bounds.top + SHOWREEL_THEATER_INSET
  const availW = Math.max(1, maxRight - minLeft)
  const availH = Math.max(1, maxBottom - minTop)
  let videoWidth = Math.max(1, availW - platePad)
  let videoHeight = videoWidth / SHOWREEL_ASPECT
  if (videoHeight > availH - platePad) {
    videoHeight = Math.max(1, availH - platePad)
    videoWidth = videoHeight * SHOWREEL_ASPECT
  }
  const width = videoWidth + platePad
  const height = videoHeight + platePad
  let left = bounds.left + (bounds.width - width) / 2
  let top = bounds.top + (bounds.height - height) / 2
  left = Math.min(Math.max(left, minLeft), maxRight - width)
  top = Math.min(Math.max(top, minTop), maxBottom - height)
  return { left, top, width, height }
}

const rectFromElement = (element) => {
  const rect = element?.getBoundingClientRect()
  if (!rect?.width || !rect?.height) return null
  return {
    left: rect.left,
    top: rect.top,
    width: rect.width,
    height: rect.height,
  }
}

const theaterTransformFromRects = (start, dest) => {
  if (!start?.width || !start?.height || !dest?.width || !dest?.height) {
    return 'translate(0px, 0px) scale(1, 1)'
  }
  const { scaleX, scaleY } = readStageScale()
  const dx = (dest.left + dest.width / 2 - (start.left + start.width / 2)) / scaleX
  const dy = (dest.top + dest.height / 2 - (start.top + start.height / 2)) / scaleY
  const sx = dest.width / start.width
  const sy = dest.height / start.height
  return `translate(${dx}px, ${dy}px) scale(${sx}, ${sy})`
}

const applyTheaterTransform = (plate, dest, start, { animate = false } = {}) => {
  if (!plate || !dest?.width || !dest?.height) return
  const transform = theaterTransformFromRects(start, dest)
  plate.style.transformOrigin = 'center center'
  if (!animate || readPrefersReducedMotion()) {
    plate.style.transition = 'none'
    plate.style.transform = transform
    return
  }
  plate.style.transition = `transform ${SHOWREEL_THEATER_MS}ms ${MOTION.ease.standard}`
  plate.style.transform = transform
}

const clearPlateFlipStyles = (plate) => {
  if (!plate) return
  plate.style.transform = ''
  plate.style.transformOrigin = ''
  plate.style.transition = ''
}

const readHeaderVideoRect = () => {
  const frame = document.querySelector('[data-project-hero] .site-media-frame')
  const rect = frame?.getBoundingClientRect()
  if (rect?.width > 8 && rect.height > 8) {
    return {
      left: rect.left,
      top: rect.top,
      width: rect.width,
      height: rect.height,
      frame,
    }
  }
  return null
}

const rectsNearlyEqual = (a, b) =>
  a
  && b
  && Math.abs(a.left - b.left) <= 0.5
  && Math.abs(a.top - b.top) <= 0.5
  && Math.abs(a.width - b.width) <= 0.5
  && Math.abs(a.height - b.height) <= 0.5

/** Pane-centred box matching the hub clip aspect when the details hero is missing. */
const paneCenterAtAspect = (pane, aspect) => {
  const safeAspect = aspect > 0 ? aspect : SHOWREEL_ASPECT
  let width = pane.width
  let height = width / safeAspect
  if (height > pane.height) {
    height = pane.height
    width = height * safeAspect
  }
  return {
    left: pane.left + (pane.width - width) / 2,
    top: pane.top + (pane.height - height) / 2,
    width,
    height,
    frame: null,
  }
}

const waitForHeaderVideoRect = (pane, aspect, timeoutMs = 1400) =>
  new Promise((resolve) => {
    const started = Date.now()
    let previous = null
    const rectInPane = (rect) => {
      if (!rect || !pane) return false
      if (rect.bottom <= pane.top || rect.top >= pane.bottom) return false
      return true
    }
    const poll = () => {
      const measured = readHeaderVideoRect()
      if (measured && rectInPane(measured)) {
        if (rectsNearlyEqual(previous, measured)) {
          resolve(measured)
          return
        }
        previous = measured
      } else {
        previous = null
      }
      if (Date.now() - started > timeoutMs) {
        resolve(paneCenterAtAspect(pane, aspect))
        return
      }
      window.requestAnimationFrame(poll)
    }
    poll()
  })

const waitForDetailsFrame = (timeoutMs = 1400) =>
  new Promise((resolve) => {
    const started = Date.now()
    const poll = () => {
      const dest = document.querySelector('[data-project-hero] video')
      if (dest && dest.readyState >= 2) {
        resolve(dest)
        return
      }
      if (Date.now() - started > timeoutMs) {
        resolve(dest || null)
        return
      }
      window.requestAnimationFrame(poll)
    }
    poll()
  })

const resumeDestinationVideo = () => {
  const destVideo = document.querySelector('[data-project-hero] video')
  if (!destVideo) {
    emitProjectExploreHandoff()
    return
  }

  const playFromStart = () => {
    try {
      destVideo.currentTime = 0
    } catch {
      // Ignore seek errors on some codecs mid-load
    }
    destVideo.play().catch(() => {})
    emitProjectExploreHandoff()
  }

  if (destVideo.readyState >= 2) {
    playFromStart()
    return
  }

  destVideo.addEventListener('loadeddata', playFromStart, { once: true })
  destVideo.addEventListener('error', emitProjectExploreHandoff, { once: true })
}

/**
 * Zoom the hub video into the case-study header video slot, then hand off.
 * Hub clip resets to frame 0 and stays frozen; project page autoplays from 0.
 * Dive stays busy until cleanup so wheel/nav is locked for the whole flight.
 */
const runVideoExploreZoom = (videoEl, onDone, onCleanup) => {
  const enterColor = readEnterColor()
  const startEl =
    videoEl.closest('.hub-flat-video-frame') ||
    videoEl.closest('.hub-flat-backplate--media') ||
    videoEl
  const rect = startEl.getBoundingClientRect()
  if (!rect.width || !rect.height) {
    onDone(enterColor)
    onCleanup?.()
    return
  }

  const pane = readMainPaneRect()
  const sourceAspect = rect.width / rect.height
  const frozenTime = 0

  videoEl.pause()
  try {
    videoEl.currentTime = frozenTime
  } catch {
    // Ignore seek errors on some codecs mid-load
  }

  const overlay = document.createElement('div')
  overlay.className = 'project-explore-overlay'
  overlay.setAttribute('aria-hidden', 'true')
  overlay.style.left = `${pane.left}px`
  overlay.style.top = `${pane.top}px`
  overlay.style.width = `${pane.width}px`
  overlay.style.height = `${pane.height}px`

  const wash = document.createElement('div')
  wash.className = 'project-explore-wash'
  wash.style.backgroundColor = enterColor

  const flyer = document.createElement('video')
  flyer.className = 'project-explore-flyer'
  flyer.src = videoEl.currentSrc || videoEl.src
  flyer.muted = true
  flyer.playsInline = true
  flyer.preload = 'auto'
  flyer.setAttribute('aria-hidden', 'true')

  flyer.style.left = `${rect.left}px`
  flyer.style.top = `${rect.top}px`
  flyer.style.width = `${rect.width}px`
  flyer.style.height = `${rect.height}px`

  overlay.appendChild(wash)
  overlay.appendChild(flyer)
  document.body.appendChild(overlay)

  const sourceVisibility = videoEl.style.visibility
  videoEl.style.visibility = 'hidden'

  let cleaned = false
  const cleanup = () => {
    if (cleaned) return
    cleaned = true
    flyer.pause?.()
    overlay.remove()
    if (videoEl.isConnected) videoEl.style.visibility = sourceVisibility
    onCleanup?.()
  }

  let zoomStarted = false
  const startZoom = () => {
    if (zoomStarted) return
    zoomStarted = true
    flyer.pause()

    wash.style.transition = 'none'
    wash.style.opacity = '1'
    onDone(enterColor)

    waitForHeaderVideoRect(pane, sourceAspect).then((destRect) => {
      let flightDone = false
      const finishFlight = () => {
        if (flightDone) return
        flightDone = true
        flyer.removeEventListener('transitionend', onFlightEnd)
        window.clearTimeout(flightFallback)
        waitForDetailsFrame().then(() => {
          resumeDestinationVideo()
          overlay.style.opacity = '0'
          window.setTimeout(cleanup, MOTION.ms.spring)
        })
      }
      const onFlightEnd = (event) => {
        if (event.target !== flyer) return
        if (event.propertyName !== 'width') return
        finishFlight()
      }

      flyer.addEventListener('transitionend', onFlightEnd)
      const flightFallback = window.setTimeout(
        finishFlight,
        MOTION.ms.flight + 100
      )

      window.requestAnimationFrame(() => {
        flyer.style.left = `${destRect.left}px`
        flyer.style.top = `${destRect.top}px`
        flyer.style.width = `${destRect.width}px`
        flyer.style.height = `${destRect.height}px`
      })
    })
  }

  const syncTimeAndZoom = () => {
    const go = () => startZoom()
    try {
      if (Math.abs((flyer.currentTime || 0) - frozenTime) > 0.04) {
        flyer.addEventListener('seeked', go, { once: true })
        flyer.currentTime = frozenTime
        window.setTimeout(go, 180)
        return
      }
    } catch {
      // Ignore seek errors on some codecs mid-load
    }
    go()
  }

  if (flyer.readyState >= 2) {
    syncTimeAndZoom()
  } else {
    flyer.addEventListener('loadeddata', syncTimeAndZoom, { once: true })
    flyer.addEventListener('error', startZoom, { once: true })
  }
}

/**
 * One persistent 3D scene for showreel, project intros, and contact.
 * Each project gets its own sliding panel so Anim-8 ↔ Sakira swaps ease
 * instead of flashing. Explore opens the full case-study page.
 */
const SceneHub = () => {
  const { pathname } = useLocation()
  const navigate = useNavigate()
  const { projectId: routeProjectId } = useParams()
  const scrollAccumRef = useRef(0)
  const scrollDragRef = useRef({ progress: 0, direction: 0 })
  const scrollTargetRef = useRef({ progress: 0, direction: 0 })
  const scrollDisplayRef = useRef({ progress: 0, direction: 0 })
  const scrollIdleTimerRef = useRef(0)
  const wheelLockedRef = useRef(false)
  const scrollSpringFrameRef = useRef(0)
  const scrollSmoothFrameRef = useRef(0)
  const hubSlideStackRef = useRef(null)
  const touchStartRef = useRef(null)
  const navigateRef = useRef(navigate)
  const lockOutApiRef = useRef(null)
  const hubRevealApiRef = useRef(null)
  const lastHubCharacterKeyRef = useRef('both')
  const prevIsHubRouteRef = useRef(null)

  const activeProject =
    featuredProjects.find((project) => project.id === routeProjectId) || null

  let activePanelKey = 'showreel'
  if (pathname === '/contact') activePanelKey = 'contact'
  else if (activeProject) activePanelKey = `project-${activeProject.id}`

  const isHubRoute =
    pathname !== '/about' && !pathname.endsWith('/details')
  const isHubRouteRef = useRef(isHubRoute)
  isHubRouteRef.current = isHubRoute

  const hubPageTitle = !isHubRoute
    ? null
    : activePanelKey === 'contact'
      ? 'Contact'
      : activeProject
        ? activeProject.title
        : 'Showreel'
  usePageTitle(hubPageTitle)

  useEffect(() => {
    setTransitionRoute({ isHubRoute })
  }, [isHubRoute])

  // Spacer collapse is a render-time fact: toggle before paint so Explore
  // measures the case-study video without a full-height spacer above it.
  useLayoutEffect(() => {
    document.body.classList.toggle('hub-passthrough-route', !isHubRoute)
    return () => {
      document.body.classList.remove('hub-passthrough-route')
    }
  }, [isHubRoute])

  useEffect(() => {
    const syncDiveClass = (snap) => {
      document.body.classList.toggle(
        'is-transition-dive',
        snap.activeKinds.includes(TRANSITION_KINDS.dive)
      )
    }
    const unsubscribe = subscribeTransitionCoordinator(syncDiveClass)
    return () => {
      unsubscribe()
      document.body.classList.remove('is-transition-dive')
    }
  }, [])

  useEffect(() => {
    let slideWasActive = hasTransitionKind(TRANSITION_KINDS.slide)
    return subscribeTransitionCoordinator((snap) => {
      const slideActive = snap.activeKinds.includes(TRANSITION_KINDS.slide)
      if (slideWasActive && !slideActive) {
        wheelLockedRef.current = false
      }
      slideWasActive = slideActive
    })
  }, [])

  useEffect(() => {
    if (!isHubRoute) wheelLockedRef.current = false
  }, [isHubRoute])

  useEffect(() => {
    const wasHubRoute = prevIsHubRouteRef.current
    prevIsHubRouteRef.current = isHubRoute
    if (wasHubRoute && !isHubRoute) {
      hubRevealApiRef.current?.leaveStage?.()
    }
    if (wasHubRoute === false && isHubRoute) {
      hubRevealApiRef.current?.restoreStagedCharacters?.()
    }
  }, [isHubRoute])

  if (isHubRoute) {
    lastHubCharacterKeyRef.current = activeProject
      ? characterKeyForProject(activeProject.id)
      : activePanelKey === 'showreel' || activePanelKey === 'contact'
        ? 'both'
        : 'sakira'
  }
  const characterKey = lastHubCharacterKeyRef.current

  const [submitState, setSubmitState] = useState('idle')
  // 'idle' | 'sending' | 'sent' | 'error'
  const [slidePark, setSlidePark] = useState(() => buildInitialSlidePark(activePanelKey))
  const [instantSlideKeys, setInstantSlideKeys] = useState(() => {
    if (readPrefersReducedMotion()) return new Set()
    return new Set([activePanelKey])
  })
  const [headingEpoch, setHeadingEpoch] = useState(0)
  const [isShowreelPlaying, setIsShowreelPlaying] = useState(false)
  const [isShowreelTheater, setIsShowreelTheater] = useState(false)
  const showreelVideoRef = useRef(null)
  const showreelPlateRef = useRef(null)
  const showreelFrameSlotRef = useRef(null)
  const showreelTheaterStartRef = useRef(null)
  const showreelTheaterDestRef = useRef(null)
  const prevSlideKeyRef = useRef(activePanelKey)
  const hasSubmittedRef = useRef(false)
  const contactFormRef = useRef(null)
  const showreelTheaterExitTimerRef = useRef(0)
  const showreelEnterTimerRef = useRef(0)
  const pendingPlayAfterTheaterRef = useRef(false)
  const showreelWatchDialogueFiredRef = useRef(false)

  const wheelNavContextRef = useRef({
    prevSlideKey: null,
    nextSlideKey: null,
    activePanelKey: 'showreel',
  })

  const fireShowreelWatchDialogue = () => {
    if (showreelWatchDialogueFiredRef.current) return
    showreelWatchDialogueFiredRef.current = true
    const repeat = hasWatchedShowreelBefore()
    emitShowreelWatch({ watching: true, repeat })
    if (!repeat) markShowreelWatched()
  }

  const playShowreelVideo = (video) => {
    if (!video) return
    video.muted = false
    const handlePlaying = () => {
      fireShowreelWatchDialogue()
      video.removeEventListener('playing', handlePlaying)
    }
    video.addEventListener('playing', handlePlaying)
    video.play().catch(() => {
      video.muted = true
      video.play().catch(() => {})
    })
  }

  const closeTheaterImmediately = () => {
    const plate = showreelPlateRef.current
    const video = showreelVideoRef.current
    window.clearTimeout(showreelTheaterExitTimerRef.current)
    window.clearTimeout(showreelEnterTimerRef.current)
    pendingPlayAfterTheaterRef.current = false
    showreelWatchDialogueFiredRef.current = false
    emitShowreelWatch({ watching: false })
    video?.pause()
    clearPlateFlipStyles(plate)
    if (isShowreelTheater) {
      setIsShowreelTheater(false)
    }
    endTransition(TRANSITION_KINDS.theater)
  }

  const handleShowreelExitTheater = () => {
    const plate = showreelPlateRef.current
    const video = showreelVideoRef.current
    window.clearTimeout(showreelTheaterExitTimerRef.current)
    window.clearTimeout(showreelEnterTimerRef.current)
    pendingPlayAfterTheaterRef.current = false
    showreelWatchDialogueFiredRef.current = false
    emitShowreelWatch({ watching: false })
    video?.pause()
    if (plate && !readPrefersReducedMotion()) {
      plate.style.transition = `transform ${SHOWREEL_THEATER_MS}ms ${MOTION.ease.standard}`
      plate.style.transformOrigin = 'center center'
      plate.style.transform = 'translate(0px, 0px) scale(1, 1)'
      showreelTheaterExitTimerRef.current = window.setTimeout(() => {
        clearPlateFlipStyles(plate)
        setIsShowreelTheater(false)
        endTransition(TRANSITION_KINDS.theater)
      }, SHOWREEL_THEATER_MS)
      return
    }
    clearPlateFlipStyles(plate)
    setIsShowreelTheater(false)
    endTransition(TRANSITION_KINDS.theater)
  }

  const handleShowreelPlay = () => {
    const video = showreelVideoRef.current
    if (!IS_DESKTOP_EXPERIENCE) {
      playShowreelVideo(video)
      return
    }

    const plate = showreelPlateRef.current
    if (!video || !plate) return

    if (isShowreelTheater) {
      playShowreelVideo(video)
      return
    }

    window.clearTimeout(showreelTheaterExitTimerRef.current)
    window.clearTimeout(showreelEnterTimerRef.current)
    pendingPlayAfterTheaterRef.current = true
    plate.style.transition = 'none'
    plate.style.transform = 'none'
    const start = plate.getBoundingClientRect()
    showreelTheaterStartRef.current = start
    showreelTheaterDestRef.current = readShowreelTheaterRect()
    beginTransition(TRANSITION_KINDS.theater)
    flushSync(() => {
      setIsShowreelTheater(true)
    })
  }

  useLayoutEffect(() => {
    if (readPrefersReducedMotion()) return undefined

    let cancelled = false
    let frame1 = 0
    let frame2 = 0

    whenOpeningStep('chrome').then(() => {
      if (cancelled) return
      const introKey = prevSlideKeyRef.current
      frame1 = window.requestAnimationFrame(() => {
        frame2 = window.requestAnimationFrame(() => {
          if (cancelled) return
          setInstantSlideKeys(new Set())
          setSlidePark((park) => ({
            ...park,
            [introKey]: 'home',
          }))
          setHeadingEpoch((epoch) => epoch + 1)
        })
      })
    })

    return () => {
      cancelled = true
      window.cancelAnimationFrame(frame1)
      window.cancelAnimationFrame(frame2)
    }
  }, [])

  useLayoutEffect(() => {
    const prev = prevSlideKeyRef.current
    if (prev === activePanelKey) return

    const prevOrder = HUB_SLIDE_KEYS.indexOf(prev)
    const nextOrder = HUB_SLIDE_KEYS.indexOf(activePanelKey)
    const goingDown = nextOrder > prevOrder
    const entry = goingDown ? 'below' : 'above'
    const exit = goingDown ? 'above' : 'below'
    prevSlideKeyRef.current = activePanelKey

    beginTransition(TRANSITION_KINDS.slide)
    let slideEnded = false
    let fallbackTimer = 0
    let slideEl = null
    const finishSlide = () => {
      if (slideEnded) return
      slideEnded = true
      window.clearTimeout(fallbackTimer)
      if (slideEl) {
        slideEl.removeEventListener('transitionend', onTransitionEnd)
      }
      endTransition(TRANSITION_KINDS.slide)
    }
    const onTransitionEnd = (event) => {
      if (event.target !== slideEl) return
      if (event.propertyName && event.propertyName !== 'transform') return
      finishSlide()
    }

    setInstantSlideKeys(new Set([activePanelKey]))
    setSlidePark((park) => ({
      ...park,
      [activePanelKey]: entry,
    }))

    const frame = window.requestAnimationFrame(() => {
      setInstantSlideKeys(new Set())
      setSlidePark((park) => ({
        ...park,
        [prev]: exit,
        [activePanelKey]: 'home',
      }))
      setHeadingEpoch((epoch) => epoch + 1)

      slideEl = hubSlideStackRef.current?.querySelector(
        `[data-hub-slide="${activePanelKey}"]`
      )
      if (slideEl) {
        slideEl.addEventListener('transitionend', onTransitionEnd)
      }
      fallbackTimer = window.setTimeout(finishSlide, MOTION.ms.nav + 100)
    })
    return () => {
      window.cancelAnimationFrame(frame)
      finishSlide()
    }
  }, [activePanelKey])

  useLayoutEffect(() => {
    if (IS_DESKTOP_EXPERIENCE) return undefined
    if (!isHubRoute) return undefined
    const stack = hubSlideStackRef.current
    if (!stack) return undefined
    const activeSlide = stack.querySelector(`[data-hub-slide="${activePanelKey}"]`)
    if (!activeSlide) return undefined

    const writeStackHeight = () => {
      stack.style.setProperty('--flat-stack-h', `${activeSlide.offsetHeight}px`)
    }

    writeStackHeight()
    const observer = new ResizeObserver(writeStackHeight)
    observer.observe(activeSlide)
    return () => observer.disconnect()
  }, [activePanelKey, isHubRoute])

  useLayoutEffect(() => {
    const nodes = [
      document.querySelector('.contact-scene-layer'),
      document.querySelector('.stage'),
      document.querySelector('.stage-ui'),
    ]
    nodes.forEach((node) => {
      node?.classList.toggle('is-showreel-theater', isShowreelTheater)
    })
    return () => {
      nodes.forEach((node) => node?.classList.remove('is-showreel-theater'))
    }
  }, [isShowreelTheater])

  useLayoutEffect(() => {
    if (!isShowreelTheater) return undefined
    const plate = showreelPlateRef.current
    const dest = showreelTheaterDestRef.current || readShowreelTheaterRect()
    if (plate) {
      plate.style.transition = 'none'
      plate.style.transformOrigin = 'center center'
      plate.style.transform = 'translate(0px, 0px) scale(1, 1)'
    }
    const start =
      rectFromElement(plate) ||
      showreelTheaterStartRef.current ||
      rectFromElement(showreelFrameSlotRef.current)

    const landedRef = { current: false }
    let landFallbackTimer = 0

    const markLanded = () => {
      if (landedRef.current) return
      landedRef.current = true
      window.clearTimeout(landFallbackTimer)
      showreelPlateRef.current?.removeEventListener('transitionend', onTransformEnd)
    }

    const onTransformEnd = (event) => {
      if (event.target !== showreelPlateRef.current) return
      if (event.propertyName !== 'transform') return
      markLanded()
    }

    let frame2 = 0
    const frame1 = window.requestAnimationFrame(() => {
      frame2 = window.requestAnimationFrame(() => {
        const nextPlate = showreelPlateRef.current
        const nextDest = dest || readShowreelTheaterRect()
        showreelTheaterStartRef.current = null
        showreelTheaterDestRef.current = null
        applyTheaterTransform(nextPlate, nextDest, start, { animate: true })
        if (readPrefersReducedMotion()) {
          markLanded()
        } else if (nextPlate) {
          nextPlate.addEventListener('transitionend', onTransformEnd)
          landFallbackTimer = window.setTimeout(markLanded, SHOWREEL_THEATER_MS + 100)
        } else {
          markLanded()
        }
        if (pendingPlayAfterTheaterRef.current) {
          const delay = readPrefersReducedMotion() ? 0 : SHOWREEL_THEATER_MS
          window.clearTimeout(showreelEnterTimerRef.current)
          showreelEnterTimerRef.current = window.setTimeout(() => {
            pendingPlayAfterTheaterRef.current = false
            playShowreelVideo(showreelVideoRef.current)
          }, delay)
        }
      })
    })

    const repositionTheater = () => {
      if (!landedRef.current) return
      const nextPlate = showreelPlateRef.current
      // Home slot is the placeholder while the plate floats (not the frame slot alone)
      const placeholder = document.querySelector('.showreel-plate-placeholder')
      const nextStart =
        rectFromElement(placeholder)
        || rectFromElement(showreelFrameSlotRef.current)
      const nextDest = readShowreelTheaterRect()
      // getBoundingClientRect forces style recalc so --stage-scale / readStageScale are current
      applyTheaterTransform(nextPlate, nextDest, nextStart, { animate: false })
    }

    let first = true
    const stageEl = document.querySelector('.stage')
    const stageObserver = new ResizeObserver(() => {
      if (first) {
        first = false
        return
      }
      repositionTheater()
    })
    if (stageEl) stageObserver.observe(stageEl)

    const handleFullscreenChange = () => {
      window.requestAnimationFrame(() => {
        repositionTheater()
      })
    }
    document.addEventListener('fullscreenchange', handleFullscreenChange)

    return () => {
      window.cancelAnimationFrame(frame1)
      window.cancelAnimationFrame(frame2)
      window.clearTimeout(landFallbackTimer)
      showreelPlateRef.current?.removeEventListener('transitionend', onTransformEnd)
      stageObserver.disconnect()
      document.removeEventListener('fullscreenchange', handleFullscreenChange)
    }
  }, [isShowreelTheater])

  useEffect(() => {
    if (activePanelKey === 'showreel') return undefined
    window.clearTimeout(showreelTheaterExitTimerRef.current)
    window.clearTimeout(showreelEnterTimerRef.current)
    pendingPlayAfterTheaterRef.current = false
    showreelWatchDialogueFiredRef.current = false
    emitShowreelWatch({ watching: false })
    showreelVideoRef.current?.pause()
    clearPlateFlipStyles(showreelPlateRef.current)
    setIsShowreelTheater(false)
    endTransition(TRANSITION_KINDS.theater)
    return undefined
  }, [activePanelKey])

  useEffect(() => {
    if (!isShowreelTheater) return undefined
    const handleKeyDown = (event) => {
      if (event.key !== 'Escape') return
      event.preventDefault()
      handleShowreelExitTheater()
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [isShowreelTheater])

  useEffect(
    () => () => {
      window.clearTimeout(showreelTheaterExitTimerRef.current)
      window.clearTimeout(showreelEnterTimerRef.current)
    },
    []
  )

  const closeTheaterImmediatelyRef = useRef(null)
  closeTheaterImmediatelyRef.current = closeTheaterImmediately

  useEffect(() => {
    navigateRef.current = navigate
    registerNavigate((path, options) => navigateRef.current(path, options))
    registerTheaterClose(() => {
      closeTheaterImmediatelyRef.current?.()
    })
  }, [navigate])

  // Explore lives in flat hub media (not CSS3D)
  useEffect(() => {
    const handleExploreClick = (event) => {
      const anchor = event.target.closest?.('a[data-explore-project]')
      if (!anchor) return

      event.preventDefault()
      event.stopPropagation()
      if (hasTransitionKind(TRANSITION_KINDS.dive)) return

      const projectId = anchor.getAttribute('data-explore-project')
      const project = featuredProjects.find((item) => item.id === projectId)
      if (!project) return

      beginTransition(TRANSITION_KINDS.dive)
      const detailsPath = getProjectDetailsPath(project.id)

      const finish = (enterColor) => {
        requestNavigation(detailsPath, {
          explicit: true,
          immediate: true,
          state: {
            enterColor,
            fromExplore: true,
          },
        })
      }

      const endDive = () => {
        endTransition(TRANSITION_KINDS.dive)
      }

      const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches
      const video =
        anchor.querySelector('video')
        || anchor.closest('[data-hub-slide]')?.querySelector('.hub-flat-video-frame video')
      if (video) {
        video.pause()
        try {
          video.currentTime = 0
        } catch {
          // Ignore seek errors on some codecs mid-load
        }
      }

      const runTransition = () => {
        if (reducedMotion || !video) {
          finish(readEnterColor())
          endDive()
          return
        }
        runVideoExploreZoom(video, finish, endDive)
      }

      const playLockOut = lockOutApiRef.current?.playLockOut
      if (reducedMotion || typeof playLockOut !== 'function') {
        runTransition()
        return
      }

      let continued = false
      const continueAfterLockOut = () => {
        if (continued) return
        continued = true
        window.clearTimeout(lockTimer)
        runTransition()
      }
      const lockTimer = window.setTimeout(
        continueAfterLockOut,
        MOTION.ms.nav / 2 + 150
      )
      playLockOut(continueAfterLockOut)
    }

    document.addEventListener('click', handleExploreClick, true)
    return () => document.removeEventListener('click', handleExploreClick, true)
  }, [])

  const mediaLayoutTuneRef = useRef(loadFlatMediaLayout(FLAT_MEDIA_LAYOUT_DEFAULTS))
  const contactLayoutTuneRef = useRef(loadContactLayout(CONTACT_LAYOUT_DEFAULTS))

  useLayoutEffect(() => {
    applyFlatMediaLayoutVars(mediaLayoutTuneRef.current)
    applyContactLayoutVars(contactLayoutTuneRef.current)
  }, [])

  useEffect(() => {
    const handleSceneReset = () => {
      mediaLayoutTuneRef.current = { ...FLAT_MEDIA_LAYOUT_DEFAULTS }
      contactLayoutTuneRef.current = { ...CONTACT_LAYOUT_DEFAULTS }
      applyFlatMediaLayoutVars(mediaLayoutTuneRef.current)
      applyContactLayoutVars(contactLayoutTuneRef.current)
    }
    window.addEventListener(HUB_SCENE_RESET_EVENT, handleSceneReset)
    return () => window.removeEventListener(HUB_SCENE_RESET_EVENT, handleSceneReset)
  }, [])

  const slideIndex = HUB_SLIDE_KEYS.indexOf(activePanelKey)
  const prevSlideKey = slideIndex > 0 ? HUB_SLIDE_KEYS[slideIndex - 1] : null
  const nextSlideKey =
    slideIndex >= 0 && slideIndex < HUB_SLIDE_KEYS.length - 1
      ? HUB_SLIDE_KEYS[slideIndex + 1]
      : null

  useEffect(() => {
    if (!IS_DESKTOP_EXPERIENCE) return undefined

    const handleKeyDown = (event) => {
      if (!isHubRouteRef.current) return
      if (event.defaultPrevented) return
      if (event.target.closest?.('input, textarea, select, [contenteditable="true"]')) {
        return
      }
      if (event.key === 'ArrowUp' && prevSlideKey) {
        event.preventDefault()
        requestNavigation(pathForHubSlide(prevSlideKey))
      }
      if (event.key === 'ArrowDown' && nextSlideKey) {
        event.preventDefault()
        requestNavigation(pathForHubSlide(nextSlideKey))
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [prevSlideKey, nextSlideKey])

  const processSubmit = async (form) => {
    if (hasSubmittedRef.current || submitState === 'sending') return

    // noValidate + manual check so submit always runs (native required would block before us)
    if (!form.checkValidity()) {
      const invalid = form.querySelector(':invalid')
      const field = invalid?.getAttribute('name') || ''
      let outcome = 'invalid'
      if (field === 'name') outcome = 'missingName'
      else if (field === 'email') {
        outcome = invalid.validity.typeMismatch ? 'invalidEmail' : 'missingEmail'
      } else if (field === 'subject') outcome = 'missingSubject'
      else if (field === 'message') outcome = 'missingMessage'
      if (CONTACT_FORM_EVENT) emitContactForm({ outcome })
      form.reportValidity()
      emitCharacterReaction('headShake')
      return
    }

    const payload = new FormData(form)
    setSubmitState('sending')

    try {
      const response = await fetch(FORMSPREE_ENDPOINT, {
        method: 'POST',
        body: payload,
        headers: { Accept: 'application/json' },
      })
      if (!response.ok) {
        throw new Error(`Formspree responded ${response.status}`)
      }
      hasSubmittedRef.current = true
      setSubmitState('sent')
      emitContactForm({ outcome: 'success' })
    } catch (error) {
      console.error('[contact] submit failed:', error)
      setSubmitState('error')
    }
  }

  useEffect(() => {
    if (submitState === 'sent') emitCharacterReaction('clap')
    if (submitState === 'error') emitCharacterReaction('headShake')
  }, [submitState])

  const ariaLabel =
    activePanelKey === 'contact'
      ? 'Contact'
      : activeProject
        ? activeProject.title
        : 'Showreel'

  const headingLanded = (key) =>
    activePanelKey === key && slidePark[key] === 'home'

  const headingEpochFor = (key) =>
    headingLanded(key) ? headingEpoch : -1

  const getSlideClassName = (key) =>
    [
      'hub-flat-media',
      key === 'contact' ? 'hub-flat-media--contact' : '',
      'hub-slide',
      `hub-slide--${slidePark[key] || 'above'}`,
      instantSlideKeys.has(key) ? 'hub-slide--instant' : '',
      key === activePanelKey ? 'is-active' : '',
    ]
      .filter(Boolean)
      .join(' ')

  const handleScrollUp = () => {
    if (!prevSlideKey) return
    requestNavigation(pathForHubSlide(prevSlideKey))
  }

  const handleScrollDown = () => {
    if (!IS_DESKTOP_EXPERIENCE && activePanelKey === 'contact') {
      requestNavigation('/about', { explicit: true })
      return
    }
    if (!nextSlideKey) return
    requestNavigation(pathForHubSlide(nextSlideKey))
  }

  const scrollUpLabel = prevSlideKey ? `Go to ${labelForHubSlide(prevSlideKey)}` : 'No previous section'
  const scrollDownLabel = nextSlideKey ? `Go to ${labelForHubSlide(nextSlideKey)}` : 'No next section'

  wheelNavContextRef.current = {
    prevSlideKey,
    nextSlideKey,
    activePanelKey,
  }

  useEffect(() => {
    scrollAccumRef.current = 0
    scrollDragRef.current = { progress: 0, direction: 0 }
    scrollTargetRef.current = { progress: 0, direction: 0 }
    scrollDisplayRef.current = { progress: 0, direction: 0 }
    window.clearTimeout(scrollIdleTimerRef.current)
    if (scrollSpringFrameRef.current) {
      window.cancelAnimationFrame(scrollSpringFrameRef.current)
      scrollSpringFrameRef.current = 0
    }
    if (scrollSmoothFrameRef.current) {
      window.cancelAnimationFrame(scrollSmoothFrameRef.current)
      scrollSmoothFrameRef.current = 0
    }
    const stack = hubSlideStackRef.current
    if (stack) {
      stack.classList.remove('is-scroll-dragging')
      stack.style.removeProperty('--hub-scroll-progress')
      stack.style.removeProperty('--hub-scroll-direction')
    }
  }, [activePanelKey])

  useEffect(() => {
    const canWheelNavigate = () =>
      isHubRouteRef.current && !wheelLockedRef.current && !isTransitionBusy()

    const applyScrollDragVisual = (progress, direction) => {
      const stack = hubSlideStackRef.current
      if (!stack) return
      scrollDragRef.current = { progress, direction }
      if (progress <= 0 || direction === 0) {
        stack.classList.remove('is-scroll-dragging')
        stack.style.removeProperty('--hub-scroll-progress')
        stack.style.removeProperty('--hub-scroll-direction')
        return
      }
      stack.classList.add('is-scroll-dragging')
      stack.style.setProperty('--hub-scroll-progress', String(progress))
      stack.style.setProperty('--hub-scroll-direction', String(direction))
    }

    const cancelScrollSmooth = () => {
      if (!scrollSmoothFrameRef.current) return
      window.cancelAnimationFrame(scrollSmoothFrameRef.current)
      scrollSmoothFrameRef.current = 0
    }

    const runScrollSmoothFrame = () => {
      scrollSmoothFrameRef.current = 0
      const target = scrollTargetRef.current
      const display = scrollDisplayRef.current

      if (target.direction !== 0) {
        display.direction = target.direction
      }

      const delta = target.progress - display.progress
      if (Math.abs(delta) > 0.0005) {
        display.progress += delta * HUB_SCROLL_SMOOTH_LERP
      } else {
        display.progress = target.progress
      }

      applyScrollDragVisual(display.progress, display.direction)

      if (Math.abs(target.progress - display.progress) > 0.0005) {
        scrollSmoothFrameRef.current = window.requestAnimationFrame(runScrollSmoothFrame)
      }
    }

    const setScrollTarget = (progress, direction) => {
      scrollTargetRef.current = { progress, direction }
      if (!scrollSmoothFrameRef.current) {
        scrollSmoothFrameRef.current = window.requestAnimationFrame(runScrollSmoothFrame)
      }
    }

    const resetScrollDrag = () => {
      scrollAccumRef.current = 0
      scrollTargetRef.current = { progress: 0, direction: 0 }
      scrollDisplayRef.current = { progress: 0, direction: 0 }
      cancelScrollSmooth()
      applyScrollDragVisual(0, 0)
    }

    const cancelScrollSpring = () => {
      if (!scrollSpringFrameRef.current) return
      window.cancelAnimationFrame(scrollSpringFrameRef.current)
      scrollSpringFrameRef.current = 0
    }

    const cancelScrollMotion = () => {
      cancelScrollSpring()
      cancelScrollSmooth()
    }

    const springBackScrollDrag = () => {
      const { progress, direction } = scrollDragRef.current
      if (progress <= 0) {
        resetScrollDrag()
        return
      }

      cancelScrollMotion()
      scrollTargetRef.current = { progress: 0, direction: 0 }
      const startProgress = progress
      const startDirection = direction
      const startTime = performance.now()

      const tick = (now) => {
        const elapsed = now - startTime
        const t = Math.min(elapsed / HUB_SCROLL_SPRING_MS, 1)
        const eased = 1 - (1 - t) ** 3
        const nextProgress = startProgress * (1 - eased)
        scrollDisplayRef.current = {
          progress: nextProgress,
          direction: startDirection,
        }
        if (t >= 1) {
          scrollSpringFrameRef.current = 0
          resetScrollDrag()
          return
        }
        applyScrollDragVisual(nextProgress, startDirection)
        scrollSpringFrameRef.current = window.requestAnimationFrame(tick)
      }

      scrollSpringFrameRef.current = window.requestAnimationFrame(tick)
      scrollAccumRef.current = 0
    }

    const scheduleScrollIdle = () => {
      window.clearTimeout(scrollIdleTimerRef.current)
      scrollIdleTimerRef.current = window.setTimeout(() => {
        if (scrollDragRef.current.progress > 0 && scrollDragRef.current.progress < 1) {
          springBackScrollDrag()
        }
      }, HUB_SCROLL_WHEEL_IDLE_MS)
    }

    const commitScrollNavigation = (direction) => {
      const { prevSlideKey: prevKey, nextSlideKey: nextKey } = wheelNavContextRef.current
      const path =
        direction > 0 && nextKey
          ? pathForHubSlide(nextKey)
          : direction < 0 && prevKey
            ? pathForHubSlide(prevKey)
            : null
      if (!path) return false

      window.clearTimeout(scrollIdleTimerRef.current)
      scrollIdleTimerRef.current = 0
      cancelScrollMotion()
      scrollAccumRef.current = 0
      scrollTargetRef.current = { progress: 0, direction: 0 }
      scrollDisplayRef.current = { progress: 0, direction: 0 }
      applyScrollDragVisual(0, 0)
      wheelLockedRef.current = true
      requestNavigation(path)
      return true
    }

    const ingestScrollDelta = (deltaY, event) => {
      if (!canWheelNavigate()) {
        if ((wheelLockedRef.current || isTransitionBusy()) && event) event.preventDefault()
        return false
      }

      cancelScrollMotion()

      if (readPrefersReducedMotion()) {
        scrollAccumRef.current += deltaY
        if (Math.abs(scrollAccumRef.current) < HUB_SCROLL_COMMIT_PX) {
          if (event) event.preventDefault()
          return true
        }
        const direction = scrollAccumRef.current > 0 ? 1 : -1
        scrollAccumRef.current = 0
        if (commitScrollNavigation(direction) && event) event.preventDefault()
        return true
      }

      scrollAccumRef.current += deltaY

      const { prevSlideKey: prevKey, nextSlideKey: nextKey } = wheelNavContextRef.current
      let direction = 0
      let progress = 0

      if (scrollAccumRef.current > 0) {
        if (!nextKey) {
          scrollAccumRef.current = 0
          springBackScrollDrag()
          return false
        }
        direction = 1
        progress = Math.min(scrollAccumRef.current / HUB_SCROLL_COMMIT_PX, 1)
      } else if (scrollAccumRef.current < 0) {
        if (!prevKey) {
          scrollAccumRef.current = 0
          springBackScrollDrag()
          return false
        }
        direction = -1
        progress = Math.min(Math.abs(scrollAccumRef.current) / HUB_SCROLL_COMMIT_PX, 1)
      }

      setScrollTarget(progress, direction)

      if (progress >= 1) {
        if (commitScrollNavigation(direction) && event) event.preventDefault()
        return true
      }

      scheduleScrollIdle()
      if (event) event.preventDefault()
      return true
    }

    const handleWheel = (event) => {
      if (event.defaultPrevented) return
      if (isHubEditableTarget(event.target)) return
      if (wheelLockedRef.current || isTransitionBusy()) {
        event.preventDefault()
        return
      }

      const deltaY = normalizeWheelDelta(event)
      if (Math.abs(deltaY) < 1) return

      ingestScrollDelta(deltaY, event)
    }

    const handleTouchStart = (event) => {
      if (isHubEditableTarget(event.target)) return
      if (event.touches.length !== 1) {
        touchStartRef.current = null
        return
      }
      const touch = event.touches[0]
      touchStartRef.current = {
        x: touch.clientX,
        y: touch.clientY,
      }
    }

    const handleTouchMove = (event) => {
      const start = touchStartRef.current
      if (!start) return
      if (isHubEditableTarget(event.target)) return
      if (event.touches.length !== 1) return

      const touch = event.touches[0]
      const deltaY = start.y - touch.clientY
      start.y = touch.clientY
      if (Math.abs(deltaY) < 0.5) return

      if (!IS_DESKTOP_EXPERIENCE) {
        const scroller = document.scrollingElement || document.documentElement
        const scrollTop = scroller.scrollTop
        if (deltaY < 0 && scrollTop > 0) return
        if (deltaY > 0 && scrollTop + window.innerHeight < scroller.scrollHeight) return
      }

      if (ingestScrollDelta(deltaY, event)) {
        event.preventDefault()
      }
    }

    const handleTouchEnd = () => {
      touchStartRef.current = null
      if (scrollDragRef.current.progress > 0 && scrollDragRef.current.progress < 1) {
        springBackScrollDrag()
      }
    }

    const resetScrollState = () => {
      window.clearTimeout(scrollIdleTimerRef.current)
      cancelScrollMotion()
      scrollAccumRef.current = 0
      scrollTargetRef.current = { progress: 0, direction: 0 }
      scrollDisplayRef.current = { progress: 0, direction: 0 }
      scrollDragRef.current = { progress: 0, direction: 0 }
    }

    if (!IS_DESKTOP_EXPERIENCE) {
      const stack = hubSlideStackRef.current
      if (!stack) return undefined
      stack.addEventListener('touchstart', handleTouchStart, { passive: true })
      stack.addEventListener('touchmove', handleTouchMove, { passive: false })
      stack.addEventListener('touchend', handleTouchEnd, { passive: true })
      stack.addEventListener('touchcancel', handleTouchEnd, { passive: true })
      return () => {
        stack.removeEventListener('touchstart', handleTouchStart)
        stack.removeEventListener('touchmove', handleTouchMove)
        stack.removeEventListener('touchend', handleTouchEnd)
        stack.removeEventListener('touchcancel', handleTouchEnd)
        resetScrollState()
      }
    }

    document.addEventListener('wheel', handleWheel, { passive: false, capture: true })
    window.addEventListener('touchstart', handleTouchStart, { passive: true })
    window.addEventListener('touchmove', handleTouchMove, { passive: false })
    window.addEventListener('touchend', handleTouchEnd, { passive: true })
    window.addEventListener('touchcancel', handleTouchEnd, { passive: true })

    return () => {
      document.removeEventListener('wheel', handleWheel, { capture: true })
      window.removeEventListener('touchstart', handleTouchStart)
      window.removeEventListener('touchmove', handleTouchMove)
      window.removeEventListener('touchend', handleTouchEnd)
      window.removeEventListener('touchcancel', handleTouchEnd)
      resetScrollState()
    }
  }, [isHubRoute])

  const hubSlides = isHubRoute ? (
        <div
          ref={hubSlideStackRef}
          className={`hub-slide-stack${
          isShowreelTheater ? ' is-showreel-theater' : ''
        }`}>
          <div
            className={getSlideClassName('showreel')}
            data-hub-slide="showreel"
            id={activePanelKey === 'showreel' ? 'hub-active-slide' : undefined}
            tabIndex={activePanelKey === 'showreel' ? -1 : undefined}
            aria-hidden={activePanelKey !== 'showreel'}
            inert={activePanelKey !== 'showreel'}
          >
            <div className="hub-slide-drag-zone">
            <HubSlideHeading
              headingEpoch={headingEpochFor('showreel')}
              isActive={activePanelKey === 'showreel'}
              title={showreelContent.title.toLowerCase()}
              copy={showreelContent.description}
              landed={headingLanded('showreel')}
            />
            <div ref={showreelFrameSlotRef} className="showreel-media-slot relative w-full">
              {isShowreelTheater ? (
                <div
                  className={`${HUB_VIDEO_PLATE_CLASS} showreel-plate-placeholder`}
                  aria-hidden="true"
                >
                  <div className="hub-flat-video-frame">
                    <div className="aspect-video w-full" />
                  </div>
                </div>
              ) : null}
              <div
                ref={showreelPlateRef}
                className={`${HUB_VIDEO_PLATE_CLASS}${
                  isShowreelTheater ? ' showreel-theater-plate' : ''
                }`}
              >
                <div className="hub-flat-video-frame relative overflow-hidden">
                  <video
                    ref={showreelVideoRef}
                    className="aspect-video w-full bg-secondary object-contain"
                    src={getMediaUrl(showreelContent.video)}
                    poster={getMediaUrl(showreelContent.poster)}
                    loop
                    playsInline
                    preload="metadata"
                    controls={isShowreelPlaying}
                    onPlay={() => setIsShowreelPlaying(true)}
                    onPause={() => setIsShowreelPlaying(false)}
                    aria-label={`${showreelContent.title} video`}
                  />
                  {isShowreelPlaying || isShowreelTheater ? null : (
                    <button
                      type="button"
                      className="absolute inset-0 z-[calc(var(--z-scene)+10)] flex items-center justify-center bg-black/35 text-white transition-opacity hover:bg-black/45 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[-4px] focus-visible:outline-white"
                      aria-label={`Play ${showreelContent.title}`}
                      onClick={handleShowreelPlay}
                    >
                      <span
                        className="flex h-16 w-16 items-center justify-center rounded-full border border-white/80 bg-black/40 pl-1"
                        aria-hidden="true"
                      >
                        <svg viewBox="0 0 16 16" className="h-7 w-7 fill-current">
                          <path d="M4 2.5v11l9-5.5-9-5.5z" />
                        </svg>
                      </span>
                    </button>
                  )}
                  {isShowreelTheater ? (
                    <button
                      type="button"
                      className="showreel-theater-close"
                      aria-label="Exit theater mode"
                      onClick={handleShowreelExitTheater}
                    >
                      X
                    </button>
                  ) : null}
                </div>
              </div>
            </div>
            {!isShowreelTheater ? (
              <div className="hub-flat-explore hub-flat-actions flex items-center justify-end gap-3">
                <HubNavUpButton
                  onClick={handleScrollUp}
                  disabled={!prevSlideKey}
                  label={scrollUpLabel}
                />
                <HubNavDownButton
                  onClick={handleScrollDown}
                  disabled={!nextSlideKey}
                  label={scrollDownLabel}
                />
                {IS_DESKTOP_EXPERIENCE ? (
                  <button
                    type="button"
                    onClick={handleShowreelPlay}
                    className="hub-flat-explore-btn site-label inline-flex px-10 py-4 text-[15px] tracking-[0.16em]"
                    aria-label={`Expand ${showreelContent.title} video`}
                  >
                    Expand
                  </button>
                ) : null}
              </div>
            ) : null}
            </div>
          </div>

          {featuredProjects.map((project) => {
            const slideKey = `project-${project.id}`
            return (
              <div
                key={slideKey}
                className={getSlideClassName(slideKey)}
                data-hub-slide={slideKey}
                id={activePanelKey === slideKey ? 'hub-active-slide' : undefined}
                tabIndex={activePanelKey === slideKey ? -1 : undefined}
                aria-hidden={activePanelKey !== slideKey}
                inert={activePanelKey !== slideKey}
              >
                <div className="hub-slide-drag-zone">
                <HubSlideHeading
                  headingEpoch={headingEpochFor(slideKey)}
                  isActive={activePanelKey === slideKey}
                  title={project.title.toLowerCase()}
                  copy={project.shortDescription}
                  landed={headingLanded(slideKey)}
                  titleClassName="hub-flat-title--project"
                />
                <div className="hub-project-preview-zone">
                <div className={HUB_VIDEO_PLATE_CLASS}>
                <a
                  href={getProjectDetailsPath(project.id)}
                  data-explore-project={project.id}
                  className="hub-flat-video-frame group relative block overflow-hidden"
                  aria-label={`Open ${project.title} project page`}
                >
                  <HubProjectVideo
                    src={getMediaUrl(project.heroVideo || project.previewVideo)}
                    poster={getMediaUrl(project.previewPoster)}
                    isActive={activePanelKey === slideKey && headingLanded(slideKey)}
                  />
                </a>
                </div>
                <div className="hub-flat-explore hub-flat-actions flex items-center justify-end gap-3">
                  <HubNavUpButton
                    onClick={handleScrollUp}
                    disabled={!prevSlideKey}
                    label={scrollUpLabel}
                  />
                  <HubNavDownButton
                    onClick={handleScrollDown}
                    disabled={!nextSlideKey}
                    label={scrollDownLabel}
                  />
                  <a
                    href={getProjectDetailsPath(project.id)}
                    data-explore-project={project.id}
                    className="hub-flat-explore-btn site-label inline-flex px-10 py-4 text-[15px] tracking-[0.16em]"
                    aria-label={`Explore ${project.title} project page`}
                  >
                    Explore
                  </a>
                </div>
                </div>
                </div>
              </div>
            )
          })}

          <div
            className={getSlideClassName('contact')}
            data-hub-slide="contact"
            id={activePanelKey === 'contact' ? 'hub-active-slide' : undefined}
            tabIndex={activePanelKey === 'contact' ? -1 : undefined}
            aria-hidden={activePanelKey !== 'contact'}
            inert={activePanelKey !== 'contact'}
          >
            <div className="hub-flat-contact">
              <div className="hub-slide-drag-zone">
              <HubSlideHeading
                headingEpoch={headingEpochFor('contact')}
                isActive={activePanelKey === 'contact'}
                title="contact"
                copy={contactContent.body}
                landed={headingLanded('contact')}
                compact
                beforeCopy={
                  <h2 className="hub-flat-kicker">{contactContent.headline}</h2>
                }
              >
                <div className="hub-flat-links flex flex-wrap items-center gap-x-2 gap-y-1">
                  <a href={`mailto:${siteInfo.email}`} className="hover:text-text-primary">
                    {siteInfo.email}
                  </a>
                  <span>/</span>
                  <a
                    href={siteInfo.linkedin}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hover:text-text-primary"
                  >
                    LinkedIn
                  </a>
                </div>
              </HubSlideHeading>

              <form
                ref={contactFormRef}
                className="hub-flat-contact-form"
                onSubmit={(event) => {
                  event.preventDefault()
                  processSubmit(event.currentTarget)
                }}
                action="#"
                method="post"
                noValidate
                aria-label="Contact form"
              >
                <ContactField3D id="name" name="name" label="Name" autoComplete="name" required />
                <ContactField3D
                  id="email"
                  name="email"
                  label="Email"
                  type="email"
                  autoComplete="email"
                  required
                />
                <ContactField3D id="subject" name="subject" label="Subject" required />
                <ContactField3D id="message" name="message" label="Message" rows={5} required />

                <input
                  type="text"
                  name="_gotcha"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  style={{ display: 'none' }}
                />

                {/* Send is never gated — sending is its job; the reaction clip is decoration and plays only if it is in. */}
                <button
                  type="submit"
                  disabled={submitState === 'sending' || submitState === 'sent'}
                  aria-disabled={submitState === 'sending' || submitState === 'sent'}
                  className="site-selection site-accent-border px-5 py-2 site-label transition-opacity hover:opacity-70 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:opacity-40"
                >
                  {submitState === 'sending'
                    ? 'Sending…'
                    : submitState === 'sent'
                      ? 'Message Sent'
                      : 'Send Message'}
                </button>

                {submitState === 'sent' && (
                  <p className="site-muted hub-flat-copy normal-case" role="status">
                    {contactContent.confirmation}
                  </p>
                )}

                {submitState === 'error' && (
                  <p className="site-muted hub-flat-copy normal-case" role="alert">
                    {contactContent.error}
                  </p>
                )}
              </form>

              <div className="hub-flat-contact-nav hub-flat-actions flex items-center justify-end gap-3">
                <HubNavUpButton
                  onClick={handleScrollUp}
                  disabled={!prevSlideKey}
                  label={scrollUpLabel}
                />
                <HubNavDownButton
                  onClick={handleScrollDown}
                  disabled={IS_DESKTOP_EXPERIENCE}
                  label={IS_DESKTOP_EXPERIENCE ? scrollDownLabel : 'Go to about'}
                />
              </div>
              </div>
            </div>
          </div>
        </div>
      ) : null

  if (!IS_DESKTOP_EXPERIENCE) {
    return (
      <>
        <section className="px-5 py-6 md:px-8 md:py-8">
          {hubSlides}
        </section>
        <Outlet />
      </>
    )
  }

  return (
    <>
      <Suspense fallback={null}>
        <ContactLevel
          activePanelKey={activePanelKey}
          characterKey={characterKey}
          lockOutApiRef={lockOutApiRef}
          hubRevealApiRef={hubRevealApiRef}
          ariaLabel={ariaLabel}
          tools={
            IS_OWNER ? (
              <>
                <PanelTransformTuner
                  tuneRef={mediaLayoutTuneRef}
                  defaults={FLAT_MEDIA_LAYOUT_DEFAULTS}
                  label="Media layout"
                  toolId="media-layout"
                  columnIndex={0}
                  showOwnerControls={IS_OWNER}
                />
                <PanelTransformTuner
                  tuneRef={contactLayoutTuneRef}
                  defaults={CONTACT_LAYOUT_DEFAULTS}
                  label="Contact layout"
                  storageKey={CONTACT_STORAGE_KEY}
                  toolId="contact-layout"
                  applyVars={applyContactLayoutVars}
                  leftMax={48}
                  widthMin={16}
                  widthMax={70}
                  columnIndex={1}
                  showOwnerControls={IS_OWNER}
                />
              </>
            ) : null
          }
        >
          {hubSlides}
        </ContactLevel>
      </Suspense>
      <Outlet />
    </>
  )
}

export default SceneHub
